/**
 * Utils mostly for use in the DateFilter
 * When working with Dates. We should assume them to be UTC whole days and keep them as DateString instances
 * The filter ranges use 'yesterday' (the last complete UTC day) as an anchor.
 * We can base the periods on 'utcNow` (`new Date()`) as we throw away the hours/minutes/seconds when converting to string.
 */
import type { ChartGranularity, DateRange } from '@/types';
import { DateString, YearString } from '@/types/state/filter';

/**
 * Returns YYYY-MM-DD for the UTC calendar day of this instant (`getUTC*`).
 */
export const toIsoDateStringUtcCalendar = (date: Date): DateString => {
  return date.toISOString().split('T')[0] as DateString;
};

export function getDayOfWeek(dateString: DateString) {
  // We do not respect locales and always consider Monday the first day of the week when filtering for data in Revenue Dashboard
  // This aligns with other Acast products, and as campaigns mostly start on Mondays it also aligns with campaign runs
  const weekStartsOn = 1;
  return (new Date(dateString).getUTCDay() + 7 - weekStartsOn) % 7;
}

export const getStartOfWeek = (dateString: DateString) =>
  addDays(dateString, -getDayOfWeek(dateString));

export function addDays(dateString: DateString, days: number): DateString {
  const date = new Date(dateString);
  date.setUTCDate(date.getUTCDate() + days);
  return toIsoDateStringUtcCalendar(date);
}

export function addMonths(dateString: DateString, months: number): DateString {
  const date = new Date(dateString);
  date.setUTCMonth(date.getUTCMonth() + months);
  return toIsoDateStringUtcCalendar(date);
}

export function firstDayOfMonth(dateString: DateString): DateString {
  const date = new Date(dateString);
  date.setUTCDate(1);
  return toIsoDateStringUtcCalendar(date);
}

export function lastDayOfMonth(dateString: DateString): DateString {
  return addDays(addMonths(firstDayOfMonth(dateString), 1), -1);
}

export function firstDayOfWeek(dateString: DateString): DateString {
  const date = new Date(dateString);
  const utcIsoDayOfWeek = (date.getUTCDay() + 6) % 7; // convert from ansi to ISO week days (monday = 0, sunday = 6)
  date.setUTCDate(date.getUTCDate() - utcIsoDayOfWeek);
  return toIsoDateStringUtcCalendar(date);
}

// Earliest selectable date when the "All time" preset flag is enabled.
export const allTimeRangeStart = '2014-01-01';

const startOf2025DateString = '2025-01-01';

/**
 * Gets the current UTC DateString
 */
export const today = () => toIsoDateStringUtcCalendar(new Date(Date.now()));
export const yesterday = () => addDays(today(), -1);

/**
 * The selectable date window: 1 Jan 2025 (or {@link allTimeRangeStart} when the "All time"
 * preset is enabled) to yesterday. Computed per call so the upper bound moves forward if the
 * tab stays open past midnight.
 * TODO: once `allTimePreset` is enabled for everyone, drop the parameter and always start from
 * {@link allTimeRangeStart}.
 */
export const getAllowedRange = (isAlltimePresetEnabled = false): Readonly<DateRange> =>
  ({
    from: isAlltimePresetEnabled ? allTimeRangeStart : startOf2025DateString,
    to: yesterday(),
  }) as const;

export const getCurrentMonthDateRange = () => {
  const to = yesterday();

  return {
    from: firstDayOfMonth(to),
    to,
  } as DateRange;
};

export const getLastTwentyEightDaysDateRange = () => {
  const to = yesterday();

  return {
    from: addDays(to, -27),
    to,
  } as DateRange;
};

export const monthNames = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
];

export const shortMonthNames = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export const getCurrentQuarter = () => {
  const currentMonth = new Date().getUTCMonth() + 1;
  return Math.ceil(currentMonth / 3);
};

export const getCurrentYear = () => new Date().getUTCFullYear().toString() as YearString;
export const formatTooltipDate = (
  dateString: DateString,
  granularity: ChartGranularity
): string => {
  const [year, month, day] = dateString.split('-');
  const monthName = shortMonthNames[parseInt(month, 10) - 1];
  const dayNum = parseInt(day, 10);

  switch (granularity) {
    case 'month':
      return `${monthName} ${year}`;
    case 'week':
      return `Week of ${monthName} ${dayNum}`;
    default:
      return `${monthName} ${dayNum}`;
  }
};
